import AddFavoriteButton from '@/src/components/UI/AddFavoriteButton';
import HomeLoanCalculation from '@/src/components/UI/HouseDetail/HomeLoanCalculation';
import ItemFeature from '@/src/components/UI/HouseDetail/ItemFeature';
import MainData from '@/src/components/UI/HouseDetail/MainData';
import MapViewSection from '@/src/components/UI/HouseDetail/MapViewSection';
import FullPage from '@/src/components/UI/FullPage';
import CopyIcon from '@/src/components/UI/CopyIcon';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import React, { FC, useMemo, useState } from 'react';
import {
  areaLabelFormatter,
  chineseNumeralFormatter,
  dealPriceLabelFormatter,
  priceLabelFormatter,
} from '@/src/libs/number-helper';
import Head from 'next/head';
import { isServer } from '@/src/libs/is-next-server';
import { IListingInBuyPage, Props } from './HouseDetailContainerProps';
import { countParkingSpace, getBuildingAge, getFloorInformation, totalSizeFormatter } from '@/utils/dataConvertor';
import { Agent, BuildingShowcaseDetailsEntity } from '@/swagger';
import classNames from 'classnames';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import { isSet } from '@/utils/formatChecker';
import Icon from '@/public/icon';
import Gallery, { GallerySection } from '@/src/components/feature/Gallery';
import NavBar from '@/src/components/UI/HouseDetail/NavBar';
import Badge from '@/src/components/UI/Badge';
import serializer from '@/src/libs/serializer';
import Flexbox from '@/components/common/Flexbox';

const cropperCss = `:root{--swiper-navigation-size:44px}.swiper-button-next,.swiper-button-prev{position:absolute;top:50%;width:calc(var(--swiper-navigation-size)/ 44 * 27);height:var(--swiper-navigation-size);margin-top:calc(0px - (var(--swiper-navigation-size)/ 2));z-index:10;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--swiper-navigation-color,var(--swiper-theme-color))}.swiper-button-next.swiper-button-disabled,.swiper-button-prev.swiper-button-disabled{opacity:.35;cursor:auto;pointer-events:none}.swiper-button-next:after,.swiper-button-prev:after{font-family:swiper-icons;font-size:var(--swiper-navigation-size);text-transform:none!important;letter-spacing:0;text-transform:none;font-variant:initial;line-height:1}.swiper-button-prev,.swiper-container-rtl .swiper-button-next{left:10px;right:auto}.swiper-button-prev:after,.swiper-container-rtl .swiper-button-next:after{content:'prev'}.swiper-button-next,.swiper-container-rtl .swiper-button-prev{right:10px;left:auto}.swiper-button-next:after,.swiper-container-rtl .swiper-button-prev:after{content:'next'}.swiper-button-next.swiper-button-white,.swiper-button-prev.swiper-button-white{--swiper-navigation-color:#ffffff}.swiper-button-next.swiper-button-black,.swiper-button-prev.swiper-button-black{--swiper-navigation-color:#000000}.swiper-button-lock{display:none}`;
const ModalPopup = dynamic(() => import('@/src/components/UI/HouseDetail/ModalPopup'), {
  loading: () => <div />,
  ssr: false,
});
// const NavBar = dynamic(() => import('@/src/components/UI/HouseDetail/NavBar'), {
//   loading: () => <div />,
//   ssr: false,
// });

const HouseDetailContainer = ({ detailData, agent, listingUrl, building }: Props) => {
  const [showLinePopup, setShowLinePopup] = useState(false);
  const [showCallMeBackForm, setShowCallMeBackForm] = useState(false);

  return (
    <FullPage
      hasPadding={false}
      sid={detailData?.sid}
      headerRight={['favorite', 'share']}
      title={detailData?.title ?? ''}
    >
      <section id={'house-info'} />
      <div className={'flex justify-center flex-col relative'}>
        <NavBar />
        <div className={'flex flex-1 flex-row px-8 w-full max-w-screen-lg  justify-center  self-center'}>
          <LeftComponent detailData={detailData} />
          <RightComponent
            setShowLinePopup={setShowLinePopup}
            setShowCallMeBackForm={setShowCallMeBackForm}
            detailData={detailData}
            agent={agent}
            listingUrl={listingUrl}
            building={building}
          />
        </div>
      </div>
      <ModalPopup
        agent={agent}
        setShowCallMeBackForm={setShowCallMeBackForm}
        setShowLinePopup={setShowLinePopup}
        showLinePopup={showLinePopup}
        showCallMeBackForm={showCallMeBackForm}
        needAddress={false}
        listingId={detailData.id}
      />
    </FullPage>
  );
};

const LeftComponent = ({ detailData }: { detailData: IListingInBuyPage }) => {
  const trafficEnv = useMemo(
    () => ({
      school: detailData.traffic_env_school,
      recreation: detailData.traffic_env_recreation,
      hospital: detailData.traffic_env_hospital,
      supermarket: detailData.traffic_env_supermarket,
      traffic: detailData.traffic_env_traffic,
      corporate: detailData.traffic_env_corporate,
    }),
    [
      detailData.traffic_env_school,
      detailData.traffic_env_recreation,
      detailData.traffic_env_hospital,
      detailData.traffic_env_supermarket,
      detailData.traffic_env_traffic,
      detailData.traffic_env_corporate,
    ]
  );

  const imageSections = useMemo(() => {
    return detailData.listing_image.reduce(
      (acc, cur) => {
        if (cur.tag === 'layout') {
          acc[1].images.push(cur.watermark_image ?? cur.original_image ?? '');
        } else {
          acc[0].images.push(cur.watermark_image ?? cur.original_image ?? '');
        }
        return acc;
      },
      [
        { key: 'default', text: undefined, images: [] },
        { key: 'layout', text: '格局圖', images: [], icon: <Icon.FloorPlanIcon /> },
      ] as Array<GallerySection>
    );
  }, [detailData.listing_image]);

  const videos = useMemo(
    () => detailData.listing_video.map((video) => video.video_url ?? ''),
    [detailData.listing_video]
  );

  return (
    <div className={'flex w-2/3 flex-col'}>
      <div className={'relative -mx-4 sm:mx-0 sm:mb-4'}>
        <Gallery imageSections={imageSections} videos={videos} />
        <Badge state={serializer(detailData.listing_state)} status={detailData.status!} sold_at={detailData.sold_at} />
      </div>
      <Caution className={'sm:block hidden sm:visible invisible'} />
      <MainData detailData={detailData} />
      <ConditionalFragment condition={detailData.country === 'US'}>
        <p className={classNames('text-sm text-gray500')}>
          1 坪 = 35.5832 平方英呎。美國物件原面積單位為「平方英呎」，轉換成「坪」會有些許落差。
        </p>
      </ConditionalFragment>
      <ItemFeature
        content={detailData.feature_content ?? ''}
        tags={detailData.feature_tags ?? []}
        hashtags={detailData.listing_hashtag}
      />
      <MapViewSection
        sid={detailData.sid}
        //@ts-ignore
        items={trafficEnv}
        country={detailData.country!}
        center={{
          //@ts-ignore
          lat: parseFloat(detailData.location_latitude ?? 0),
          //@ts-ignore
          lng: parseFloat(detailData.location_longitude ?? 0),
        }}
      />
      <HomeLoanCalculation price={detailData.price ?? 0} country={detailData.country!} />
    </div>
  );
};

export const Caution: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <p className={classNames(className, 'text-xs text-gray500 font-medium')}>
      本物件照片中之相關裝飾或其他物品，僅係提供買方做為參考之用，並不隨同該物件買賣或贈與。於簽訂相關買賣契約前，請您詳細確認附隨買賣之設備或家飾、贈品。
    </p>
  );
};

const RightComponent = ({
  detailData,
  agent,
  setShowLinePopup,
  setShowCallMeBackForm,
  listingUrl,
  building,
}: {
  detailData: IListingInBuyPage;
  agent: Agent;
  setShowLinePopup: (value: boolean) => void;
  setShowCallMeBackForm: (value: boolean) => void;
  listingUrl: string;
  building: BuildingShowcaseDetailsEntity | null;
}) => {
  // const t = useT('agents');
  const { price, dealPrice, title, room, common_space, bath, country } = detailData;

  const isTWObject = country === 'TW';

  const houseStatusData = useMemo(() => {
    return [
      dealPrice
        ? {
            name: dealPriceLabelFormatter(country!),
            content: chineseNumeralFormatter(dealPrice ?? 0),
          }
        : undefined,
      { name: priceLabelFormatter(country!), content: chineseNumeralFormatter(price ?? 0) },
      {
        name: '格局',
        content: room || common_space || bath ? `${room ?? 0} 房 ${common_space ?? 0} 廳 ${bath ?? 0} 衛` : '-',
      },
      //@ts-ignore
      {
        name: areaLabelFormatter(country!),
        content: totalSizeFormatter(detailData.detail_total_area_size, detailData.country),
      },
      { name: '類型', content: detailData.detail_category || '-' },
      { name: '樓層/樓高', content: getFloorInformation(detailData) },
      { name: '屋齡', content: getBuildingAge(detailData) },
      { name: '車位', content: countParkingSpace(detailData) },
    ].filter(isSet);
  }, [detailData]);

  // const agentServiceLocation = useMemo(() => {
  //   if (country === 'TW') return null;
  //   const serviceLocations = agent?.agent_service_location ?? [];
  //   return serviceLocations?.find(({ location }) => location?.startsWith(country ?? '')) ?? serviceLocations[0];
  // }, [agent, country]);

  return (
    <>
      <Head>
        <style>{cropperCss}</style>
      </Head>
      <div className={'flex w-1/3 pl-6 flex-col bg-white'}>
        <div className={'flex flex-col w-full'}>
          <div className={'flex flex-row justify-between items-center'}>
            <div className={'flex flex-row justify-start my-2 items-center'}>
              <p className='text-lg leading-loose font-medium text-dark-grey uppercase text-gray900'>{title}</p>
              {detailData.display_building_project && detailData.building_project !== '' && (
                <>
                  <div className={'w-px bg-gray700 mx-3'} style={{ height: '1rem' }} />
                  <p className='text-lg leading-loose font-medium text-dark-grey uppercase text-gray900'>
                    {detailData.building_project}
                  </p>
                </>
              )}
            </div>
            <div className={'flex flex-row items-center invisible md:visible'}>
              <AddFavoriteButton sid={detailData.sid} />
              {!isServer() ? (
                <CopyIcon text={listingUrl} />
              ) : (
                <div
                  data-tip='複製連結'
                  className={'w-12 h-12 hover:bg-gray100 flex items-center justify-center -mr-3'}
                >
                  <Image width={24} height={24} className={'w-6 h-6 '} src={'/icons-share-filled.png'} />
                </div>
              )}
            </div>
          </div>
          <Flexbox align={'center'} justify={'between'} className={'text-sm leading-normal uppercase text-gray700'}>
            <p>{detailData.display_address}</p>
            <p>{detailData.sid}</p>
          </Flexbox>
          <div className={'flex flex-col w-full'}>
            {building && (
              <ItemDescription
                name={'建案'}
                value={
                  <Link href={`/building/${building.sid}`} passHref>
                    <a className={'text-fb-blue underline'}>{building.adTitle}</a>
                  </Link>
                }
              />
            )}
            {houseStatusData.map((item, idx) => {
              return (
                <ItemDescription
                  key={`ItemDescription-${idx}-${item.name}-${item.content}`}
                  name={item.name}
                  value={item.content ?? ''}
                />
              );
            })}
          </div>
        </div>
        <div className={'flex mt-8 w-full border border-gray300 px-4 pt-3 pb-4 flex-col  sticky top-16'}>
          <p className='text-lg leading-loose font-medium text-dark-grey uppercase cursor-pointer'>
            聯絡房產顧問
            {/*{agentServiceLocation && t(`location.${agentServiceLocation.location}`)}*/}
          </p>
          {isTWObject && (
            <div className={'py-4 flex flex-row'}>
              <div>
                <Image src={agent?.pictures?.[0] ?? ''} width={80} height={80} className={'rounded-full'} />
              </div>
              <div className={'flex-col flex pl-3'}>
                <p className='text-lg leading-loose font-medium text-dark-grey'>{agent?.name}</p>
                <div className={'flex flex-row items-center'}>
                  <Link href={`/agent/${agent?.sid}`}>
                    <a className='text-sm leading-normal tracking-wide uppercase text-gray700 underline'>了解更多</a>
                  </Link>
                  <IconBtn
                    toolTipString={'掃描 QR 碼'}
                    onPress={() => {
                      setShowLinePopup(true);
                    }}
                    iconURL={'/icons-line-white.png'}
                  />
                  <IconBtn
                    toolTipString={'回電給我'}
                    onPress={() => {
                      setShowCallMeBackForm(true);
                    }}
                    iconURL={'/icons-contact.png'}
                  />
                </div>
              </div>
            </div>
          )}
          <div className={'flex flex-row w-full py-2 mb-2 space-x-2'}>
            <Image width={24} height={24} className={'w-6 h-6'} src={'/icons-call.png'} />
            <p className={'text-xl leading-6 text-brand font-bold uppercase'}>
              {isTWObject ? agent?.contact_phone : '(總機) 02-7752-3755'}
            </p>
            {!isTWObject && (
              <div className={'flex items-center space-x-4'}>
                <IconBtn
                  toolTipString={'掃描 QR 碼'}
                  size={'sm'}
                  onPress={() => {
                    setShowLinePopup(true);
                  }}
                  iconURL={'/icons-line-white.png'}
                />
                <IconBtn
                  toolTipString={'回電給我'}
                  size={'sm'}
                  onPress={() => {
                    setShowCallMeBackForm(true);
                  }}
                  iconURL={'/icons-contact.png'}
                />
              </div>
            )}
          </div>
          <p className='text-xs leading-normal tracking-wide font-medium text-gray500'>
            預約看房或諮詢，立即撥給 {isTWObject ? agent?.name : '客服專員'}
          </p>
        </div>
      </div>
    </>
  );
};

interface IconBtnProps {
  onPress: () => void;
  iconURL: string;
  toolTipString: string;
  size?: 'sm' | 'md';
}

const IconBtn: FC<IconBtnProps> = ({ onPress, iconURL, toolTipString, size = 'md' }) => {
  return (
    <div
      data-tip={toolTipString}
      onClick={onPress}
      className={classNames('hover:bg-gray100 items-center justify-center flex ml-1 cursor-pointer', {
        'w-12 h-12': size === 'md',
        'w-6 h-6': size === 'sm',
      })}
    >
      <Image width={24} height={24} src={iconURL} />
    </div>
  );
};
const ItemDescription = ({ name, value }: { name: string; value: React.ReactNode }) => {
  return (
    <div className={'w-full flex flex-row justify-between items-start py-2 border-b  border-gray300 mb-2'}>
      <p className='leading-normal tracking-wide uppercase text-gray700 '>{name}</p>
      <p className='leading-normal tracking-wide text-dark-grey uppercase text-gray900 whitespace-pre text-right'>
        {value}
      </p>
    </div>
  );
};

export default HouseDetailContainer;
